import { useMemo } from 'react';

/**
 * Event display info extracted from HDE page modules.
 * Used in the registration modal left panel to show date and location.
 */
interface HDEEventDisplayInfo {
    /** Formatted date string (e.g. "FRIDAY 22 MAY") */
    dateDisplay: string | null;
    /** Formatted location string (e.g. "SHED 10, AUCKLAND") */
    locationDisplay: string | null;
}

interface EventWithDateAndLocation {
    Title?: string | null;
    Location?: string | null;
    Area?: string | null;
    area?: string | null; // camelCase variant from some GraphQL clients
    RegistrationFormTitle?: string | null;
    registrationFormTitle?: string | null;
    DateTimeDescription?: string | null;
    Days?: {
        edges?: Array<{
            node: {
                Date?: string | null;
                DateISO?: string | null;
                StartTime?: string | null;
                EndTime?: string | null;
                DateTimeDescription?: string | null;
            };
        }>;
    };
}

/**
 * Extracts event date and location from HDE page modules for display in the registration modal.
 * Checks EventRegisterModule for the first active event.
 */
export function useHDEEventDisplayInfo(
    modules: unknown[],
): HDEEventDisplayInfo {
    return useMemo(() => {
        const event = extractFirstActiveEvent(modules);
        if (!event) {
            return { dateDisplay: null, locationDisplay: null };
        }

        const dateDisplay = getDateDisplay(event);
        const locationDisplay = getLocationDisplay(event);

        return { dateDisplay, locationDisplay };
    }, [modules]);
}

function extractFirstActiveEvent(
    modules: unknown[],
): EventWithDateAndLocation | null {
    for (const module of modules || []) {
        if (
            typeof module !== 'object' ||
            module === null ||
            !('__typename' in module)
        ) {
            continue;
        }

        if (module.__typename === 'EventRegisterModule' && 'Events' in module) {
            const events =
                (
                    module as {
                        Events?: {
                            edges?: Array<{
                                node: EventWithDateAndLocation & {
                                    RegistrationStatus?: string;
                                };
                            }>;
                        };
                    }
                ).Events?.edges || [];
            const active = events.find(
                (e) => e.node?.RegistrationStatus === 'active',
            );
            if (active?.node) return active.node;
        }
    }
    return null;
}

/** Format to display in "FRIDAY 22 MAY" style (uppercase day name, day number, uppercase month) */
const DATE_DISPLAY_FORMAT = /^[A-Z][A-Za-z]+\s+\d{1,2}\s+[A-Z][A-Za-z]+$/;

function formatDateToDisplay(raw: string): string {
    const trimmed = raw.trim();
    if (DATE_DISPLAY_FORMAT.test(trimmed)) {
        return trimmed.toUpperCase();
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
        return trimmed.toUpperCase();
    }
    const dayName = parsed
        .toLocaleDateString('en-NZ', { weekday: 'long' })
        .toUpperCase();
    const day = parsed.getDate();
    const month = parsed
        .toLocaleDateString('en-NZ', { month: 'short' })
        .toUpperCase();
    return `${dayName} ${day} ${month}`;
}

function getDateDisplay(event: EventWithDateAndLocation): string | null {
    const firstDay = event.Days?.edges?.[0]?.node;
    const raw =
        event.DateTimeDescription ??
        firstDay?.DateTimeDescription ??
        firstDay?.Date ??
        firstDay?.DateISO ??
        event.Title ??
        null;
    if (!raw) return null;
    return formatDateToDisplay(raw);
}

function getLocationDisplay(event: EventWithDateAndLocation): string | null {
    const location = event.Location ?? null;
    const area = event.Area ?? event.area ?? null;
    const regTitle = (
        event.RegistrationFormTitle ??
        event.registrationFormTitle ??
        ''
    ).trim();

    const locationAndArea = [location, area].filter(Boolean);
    if (locationAndArea.length > 0) {
        return locationAndArea.join(', ');
    }
    if (regTitle) {
        return regTitle;
    }
    return null;
}
